<template>
  <div v-if="adaptiveImage" ref="root" class="adaptive-container">
    <img
      v-bind="{ ...$attrs, attrs }"
      :src="imagePlaceholder || formalizedSrc"
      :srcset="imagePlaceholder || srcset"
      class="adaptive-image"
    />
  </div>
  <picture v-else-if="srcWebp" ref="root" class="adaptive-container">
    <source type="image/webp" :srcset="imagePlaceholder || srcsetWebP" />
    <img
      :src="imagePlaceholder || formalizedSrc"
      :srcset="imagePlaceholder || srcset"
      v-bind="{ ...$attrs, attrs }"
      class="adaptive-image"
    />
  </picture>
  <img
    v-else
    ref="root"
    v-bind="{ ...$attrs, attrs }"
    :src="imagePlaceholder || formalizedSrc"
    :srcset="imagePlaceholder || srcset"
    class="adaptive-image"
  />
</template>

<script lang="ts" setup>
import { useIntersectionObserver } from '@vueuse/core';
import type { ImageSize } from '~/interfaces/app';
import { useMediaQueryStore } from '~/stores/mediaQuery';

export interface ImageCut {
  startSrc: string;
  endSrc: string;
}

const formalizedSrc = ref('/static/images/no-img.svg');
const srcset = ref('');
const srcsetWebP = ref('');
const root = ref<HTMLElement>();
const isInit = ref(false);
const mediaStore = useMediaQueryStore();
const isMobile = computed(() => mediaStore.isMobile);

const props = defineProps({
  src: {
    type: String,
    default: '',
  },
  srcWebp: {
    type: String,
    default: '',
  },
  imagePlaceholder: String,
  mobileSizes: {
    type: Object as () => ImageSize,
    default: () => {},
  },
  desktopSizes: {
    type: Object as () => ImageSize,
    default: () => {},
  },
  loadOriginal: {
    type: Boolean,
    default: false,
  },
  adaptiveImage: {
    type: Boolean,
    default: false,
  },
  lazy: {
    type: Boolean,
    default: false,
  },
});
const buildPathSrc = (start: string, end: string, sizes: ImageSize): string => {
  return end ? `${start}/${sizes.width}x${sizes.height}/${end}` : start;
};

function splitImage(src: string, delimiter = '/-/'): ImageCut {
  const [startSrc, ...end] = src
    ? src.split(delimiter)
    : ['/static/images/no-img.svg'];
  const endSrc = end.join(delimiter);

  return { startSrc, endSrc };
}

function getSrcImages(sizes: ImageSize, ...images: ImageCut[]): string {
  return images.reduce((srcset, currentValue, index) => {
    const scaleSizes = (sizes: ImageSize, multiplier: number) => ({
      height: sizes.height * multiplier,
      width: sizes.width * multiplier,
    });
    const start = srcset === '' ? srcset : srcset + ',';
    const image = buildPathSrc(
      currentValue.startSrc,
      currentValue.endSrc,
      scaleSizes(sizes, index + 1)
    );
    const multiple = index === 0 ? '' : ` ${index + 1}x`;

    return `${start} ${image}${multiple}`;
  }, '');
}

const sizes = isMobile.value ? props.mobileSizes : props.desktopSizes;
const attrs = reactive({
  width: sizes?.width,
  height: sizes?.height,
});

const init = () => {
  isInit.value = true;
  const httpReg = /^https?:/;
  const imagesSrc = props.src ? props.src.replace(httpReg, '') : '';
  if (props.loadOriginal) {
    formalizedSrc.value = imagesSrc;
  } else {
    const prepareImage = splitImage(imagesSrc);
    formalizedSrc.value = buildPathSrc(
      prepareImage.startSrc,
      prepareImage.endSrc,
      sizes
    );
    srcset.value = getSrcImages(sizes, prepareImage, prepareImage);

    if (props.srcWebp) {
      const imagesWebPSrc = props.srcWebp
        ? props.srcWebp.replace(httpReg, '')
        : '';
      const prepareImageWebp = splitImage(imagesWebPSrc);
      srcsetWebP.value = getSrcImages(
        sizes,
        prepareImageWebp,
        prepareImageWebp
      );
    }
  }
};

if (!props.lazy) {
  init();
}

const { stop } = useIntersectionObserver(root, ([{ isIntersecting }]) => {
  if (isIntersecting && !isInit.value) init();
});

watch(
  () => props.src,
  () => init()
);
onUnmounted(() => {
  stop();
});
</script>
<style lang="scss">
.adaptive-image {
  object-position: center;
  mix-blend-mode: multiply;
}

.adaptive-container {
  width: 100%;
  height: 100%;

  .adaptive-image {
    position: relative;
    top: 50%;
    left: 50%;
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    object-position: center;
    transform: translate(-50%, -50%);
  }
}
</style>
